/**
 * ProfilePage
 *
 * Page de profil utilisateur affichant les informations personnelles,
 * le rôle, les permissions et l'activité récente.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Skeleton
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useRBAC } from '../contexts/RBACContext';
import {
  UserRole,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  ROLE_HIERARCHY,
  requiresClassAssignment
} from '../types/rbac';
import AdminUsersApiService from '../services/adminUsersApi';
import CurriculumApiService from '../services/curriculumApi';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { formatDate } from '../utils/dates';
import { logger } from '../lib/logger';

// Couleurs des rôles
const ROLE_COLORS: Record<UserRole, 'error' | 'warning' | 'info' | 'success'> = {
  [UserRole.SUPER_ADMIN]: 'error',
  [UserRole.ADMIN]: 'warning',
  [UserRole.LECTEUR]: 'success'
};

interface ProfileData {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: UserRole;
  assignedClasses?: string[];
  status: string;
  createdAt: string | Date;
  lastLogin?: string | Date;
  metadata?: {
    notes?: string;
    [key: string]: unknown;
  };
}

interface ClassInfo {
  id: string;
  nom: string;
}

const ProfilePage: React.FC = () => {
  const { environment } = useEnvironment();
  const { user } = useAuth();
  const { claims, refreshClaims } = useRBAC();

  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [classesInfo, setClassesInfo] = useState<ClassInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les données du profil
  const loadProfile = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      setError(null);

      // Récupérer les données utilisateur depuis l'API
      const response = await AdminUsersApiService.getMyProfile();

      if (response?.success && response?.data) {
        const userData = response.data;
        setProfileData(userData);

        // Charger les infos des classes assignées
        const assignedClasses = userData.assignedClasses;
        if (assignedClasses && assignedClasses.length > 0) {
          const allClasses = await CurriculumApiService.getClasses(environment);
          const assigned = allClasses.filter((classe) =>
            assignedClasses.includes(classe.id)
          );
          setClassesInfo(assigned);
        }
      } else {
        // Utiliser les données Firebase si l'API ne retourne pas de données
        setProfileData({
          id: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || undefined,
          role: claims?.role || UserRole.LECTEUR,
          assignedClasses: claims?.classes || [],
          status: 'active',
          createdAt: new Date().toISOString()
        });
      }
    } catch (err: unknown) {
      logger.error('Error loading profile:', err);
      // Fallback sur les données Firebase
      setProfileData({
        id: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || undefined,
        role: claims?.role || UserRole.LECTEUR,
        assignedClasses: claims?.classes || [],
        status: 'active',
        createdAt: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  }, [user, claims, environment]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Formater la date

  // Rafraîchir les claims
  const handleRefreshClaims = async () => {
    try {
      await refreshClaims();
      await loadProfile();
    } catch (err) {
      setError('Erreur lors du rafraîchissement');
    }
  };

  if (loading) {
    return (
      <Box p={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
              <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
              <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Skeleton variant="text" width="30%" height={40} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Impossible de charger les données du profil
        </Alert>
      </Box>
    );
  }

  const role = profileData.role || claims?.role || UserRole.LECTEUR;
  const showClasses = requiresClassAssignment(role);

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Mon Profil
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshClaims}
        >
          Actualiser
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Carte Profil Principal */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={profileData.photoURL || user?.photoURL || undefined}
              alt={profileData.displayName}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                fontSize: '3rem',
                bgcolor: 'primary.main'
              }}
            >
              {profileData.displayName?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>

            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {profileData.displayName || 'Utilisateur'}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {profileData.email}
            </Typography>

            <Box mt={2}>
              <Chip
                label={ROLE_LABELS[role]}
                color={ROLE_COLORS[role]}
                icon={<SecurityIcon />}
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            <Box mt={1}>
              <Chip
                label={profileData.status === 'active' ? 'Actif' : 'Désactivé'}
                color={profileData.status === 'active' ? 'success' : 'default'}
                size="small"
                icon={profileData.status === 'active' ? <CheckCircleIcon /> : <BlockIcon />}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary" textAlign="left">
              {ROLE_DESCRIPTIONS[role]}
            </Typography>
          </Paper>
        </Grid>

        {/* Informations détaillées */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Informations du compte */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    Informations du compte
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <BadgeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Identifiant"
                        secondary={profileData.id}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={profileData.email}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <SecurityIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Rôle"
                        secondary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={ROLE_LABELS[role]}
                              color={ROLE_COLORS[role]}
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              (Niveau {ROLE_HIERARCHY[role]}/4)
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Membre depuis"
                        secondary={formatDate(profileData.createdAt, 'dateTimeLong', 'Non disponible')}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <AccessTimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Dernière connexion"
                        secondary={formatDate(profileData.lastLogin, 'dateTimeLong', 'Non disponible')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Classes assignées (si applicable) */}
            {showClasses && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon color="primary" />
                      Classes assignées
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {classesInfo.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {classesInfo.map((classe) => (
                          <Chip
                            key={classe.id}
                            label={classe.nom || classe.id}
                            icon={<SchoolIcon />}
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </Box>
                    ) : profileData.assignedClasses?.length ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {profileData.assignedClasses.map((classId) => (
                          <Chip
                            key={classId}
                            label={classId}
                            icon={<SchoolIcon />}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="info">
                        Aucune classe assignée. Contactez un administrateur pour obtenir l'accès aux classes.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Permissions */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    Permissions
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    {role === UserRole.SUPER_ADMIN && (
                      <Grid item xs={12}>
                        <Alert severity="info" icon={<SecurityIcon />}>
                          <strong>Super Administrateur</strong> - Accès complet à toutes les fonctionnalités du système
                        </Alert>
                      </Grid>
                    )}

                    {role === UserRole.ADMIN && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Gestion du contenu
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Créer, modifier et supprimer les chapitres, leçons et exercices
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Gestion des utilisateurs
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Créer et gérer les lecteurs
                            </Typography>
                          </Paper>
                        </Grid>
                      </>
                    )}

                    {role === UserRole.LECTEUR && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Lecture seule
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Consulter le contenu pédagogique des classes assignées
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
